import type { Maybe } from '@xing-com/crate-common-graphql-types';

export const findReasonCombinationOrPrioritizedSingle = (
  reasons: Array<string>
): Maybe<string> => {
  // Note: Order of allowed combinations is important
  const possibleCombinations = [
    ['EVENTS', 'GROUPS'],
    ['COMPANIES', 'GROUPS'],
    ['COMPANIES', 'CONTACTS'],
    ['COMPANIES', 'EVENTS'],
    ['COMPANIES_PAST', 'GROUPS'],
    ['COMPANIES_PAST', 'CONTACTS'],
    ['COMPANIES_PAST', 'EVENTS'],
    ['EVENTS', 'CONTACTS'],
    ['CONTACTS', 'GROUPS'],
  ];
  const [reasonA, reasonB] =
    possibleCombinations.find(
      ([possibleReasonA, possibleReasonB]) =>
        reasons.includes(possibleReasonA) && reasons.includes(possibleReasonB)
    ) ?? [];

  if (reasonA && reasonB) {
    return `${reasonA}_${reasonB}`;
  }

  // Note: Order of single reasons is important
  const singleReasonOrder = [
    'EVENTS',
    'GROUPS',
    'COMPANIES',
    'COMPANIES_PAST',
    'CONTACTS',
    'EMAIL_CONTACT',
  ];

  return singleReasonOrder.find((reason) => reasons.includes(reason)) || null;
};

export const mapReasonsToTextTokens = (
  reasons: Array<Maybe<string>>
): Array<string> => {
  if (reasons.length <= 0) {
    return [];
  }

  const transformMap = new Map([
    ['address-book-of-user', 'EMAIL_CONTACT'],
    ['sharedcontacts', 'CONTACTS'],
    ['coworker', 'COMPANIES_PAST'],
    ['coworker-current', 'COMPANIES'],
    ['coworker-recent', 'COMPANIES'],
    ['events', 'EVENTS'],
    ['groups', 'GROUPS'],
  ]);

  return Array.from(
    new Set(
      reasons
        .filter((reason) => transformMap.has(reason || ''))
        .map((reason) => transformMap.get(reason || '') || '')
    )
  );
};

export const pluralizationWithFallback = (count: number): string =>
  count === 1 ? 'ONE' : count > 1 ? 'MANY' : 'PRIVATE';

export const getReasonCombinationOrSingleText = (
  reasons: Array<Maybe<string>>,
  numberOfSharedContacts: number
): string | null => {
  if (!reasons || reasons.length === 0) {
    return null;
  }

  const textTokens = mapReasonsToTextTokens(reasons);
  const reasonCombinations =
    findReasonCombinationOrPrioritizedSingle(textTokens);

  if (!reasonCombinations) {
    return null;
  }

  if (reasonCombinations.includes('CONTACTS')) {
    // for contacts related recos we want to show the number of shared contacts
    return `NETWORK_MYNETWORK_RECOMMENDATIONS_${reasonCombinations}_${pluralizationWithFallback(
      numberOfSharedContacts
    )}`;
  } else {
    return `NETWORK_MYNETWORK_RECOMMENDATIONS_${reasonCombinations}`;
  }
};
